import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import { Image } from '@codes/ui-kit/components/ui/image';
import { Loader2, MessageSquare, SendHorizontal } from 'lucide-react';

import { AiLogoDarkImage, AiLogoImage } from '@codes/assets';

import { ChatMessage } from './ChatMessage';

const MAX_MESSAGES = 10;
const MAX_USER_MESSAGES = 5;

interface ChatMessageData {
    id: string;
    content: string;
    isUser: boolean;
    timestamp: Date;
    usage?: {
        promptToken?: number;
        completionToken?: number;
        totalToken?: number;
    };
}

interface ResponseTestDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    response: any | null;
    isLoading: boolean;
    testInput: string;
    onTestInputChange: (input: string) => void;
    onSendTest: () => void;
    error?: string | null;
}

export const ResponseTestDialog: React.FC<ResponseTestDialogProps> = ({
    isOpen,
    onOpenChange,
    response,
    isLoading,
    testInput,
    onTestInputChange,
    onSendTest,
    error,
}) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<ChatMessageData[]>([]);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (isOpen) {
            setMessages([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (response && !isLoading) {
            const aiMessage: ChatMessageData = {
                id: `ai-${Date.now()}`,
                content:
                    response.output?.content ||
                    response.choices?.[0]?.message?.content ||
                    response.content ||
                    t('agent.form.responseTest.noResponse', '응답 없음'),
                isUser: false,
                timestamp: new Date(),
                usage: response.usage
                    ? {
                          promptToken: response.usage.promptToken || response.usage.prompt_tokens,
                          completionToken: response.usage.completionToken || response.usage.completion_tokens,
                          totalToken: response.usage.totalToken || response.usage.total_tokens,
                      }
                    : undefined,
            };
            setMessages(prev => [...prev, aiMessage]);
        }
    }, [response, isLoading, t]);

    useEffect(() => {
        if (error && !isLoading) {
            const errorMessage: ChatMessageData = {
                id: `error-${Date.now()}`,
                content: error,
                isUser: false,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        }
    }, [error, isLoading]);

    useEffect(() => {
        if (isOpen) {
            setMessages([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLoading]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    }, [testInput]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (testInput.trim() && !isLoading) {
                handleSendMessage();
            }
        }
    };

    const handleSendMessage = () => {
        if (!testInput.trim() || isLoading) {
            return;
        }

        if (messages.length > MAX_MESSAGES) {
            return;
        }

        const userMessage: ChatMessageData = {
            id: `user-${Date.now()}`,
            content: testInput,
            isUser: true,
            timestamp: new Date(),
        };
        setMessages(prev => [...prev, userMessage]);

        onTestInputChange('');
        onSendTest();
    };

    const convertToChatView = (message: ChatMessageData) => ({
        id: message.id,
        content: message.content,
        stereo: (message.isUser ? 'query' : 'answer') as 'query' | 'answer',
        createdAt: message.timestamp.getTime(),
        usage$: message.usage,
        isError: message.id.startsWith('error-'),
    });

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent
                className="p-0 max-w-[600px] h-[80vh] flex flex-col"
                onPointerDownOutside={e => e.preventDefault()}
            >
                <DialogHeader className="px-6 py-4">
                    <DialogTitle className="flex items-center gap-2 text-lg font-semibold justify-center">
                        {t('agent.form.responseTest.title', 'Agent 응답 테스트')}
                    </DialogTitle>
                    <div className="text-xs text-muted-foreground">
                        {t('agent.form.responseTest.limitInfo', { count: MAX_USER_MESSAGES })}
                    </div>
                </DialogHeader>

                <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#1C1C1D]">
                    {/* 채팅 영역 */}
                    <div ref={chatContainerRef} className="flex-1 overflow-auto p-6 scroll-smooth">
                        <div className="mx-auto space-y-4">
                            {/* 빈 상태 */}
                            {messages.length === 0 && !isLoading && (
                                <div className="flex items-center justify-center py-12">
                                    <div className="text-center max-w-md mx-auto">
                                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                                            <MessageSquare className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                        <div className="text-muted-foreground text-base whitespace-pre-line leading-relaxed">
                                            {t('agent.form.responseTest.emptyState.title', '테스트를 시작해보세요')}
                                            <br />
                                            {t(
                                                'agent.form.responseTest.emptyState.description',
                                                '메시지를 입력하고 답변을 확인해보세요'
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {messages.map(message => (
                                <ChatMessage key={message.id} message={convertToChatView(message)} />
                            ))}

                            {isLoading && (
                                <div className="flex items-center gap-2 relative group max-w-[85%] text-title p-[10px] rounded-[18px]">
                                    <Image
                                        src={AiLogoImage}
                                        darkSrc={AiLogoDarkImage}
                                        className="w-5 h-5 rounded-full"
                                        alt="AI Assistant"
                                    />
                                    <div className="flex items-center gap-1 p-3 rounded-lg">
                                        <div className="animate-bounce">•</div>
                                        <div className="animate-bounce [animation-delay:0.2s]">•</div>
                                        <div className="animate-bounce [animation-delay:0.4s]">•</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 입력 영역 */}
                    <div className="px-4 py-3 bg-white dark:bg-[#1C1C1D]">
                        <div className="flex items-end gap-3 mx-auto">
                            <div className="flex-1 relative">
                                <textarea
                                    ref={textareaRef}
                                    value={testInput}
                                    onChange={e => onTestInputChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={
                                        messages.length >= MAX_MESSAGES
                                            ? t(
                                                  'agent.form.responseTest.maxLimitReached',
                                                  '최대 테스트 수에 도달했습니다'
                                              )
                                            : t('agent.form.responseTest.inputPlaceholder', '메시지를 입력하세요...')
                                    }
                                    className="w-full resize-none outline-none bg-transparent text-sm leading-relaxed pr-12 py-2 max-h-[120px] min-h-[44px] border border-chatbot-line rounded-lg px-3 focus:border-point transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    rows={2}
                                    disabled={isLoading || messages.length >= MAX_MESSAGES}
                                />
                                <div className="absolute right-2 bottom-3">
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={!testInput.trim() || isLoading || messages.length >= MAX_MESSAGES}
                                        className="flex-shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <Loader2 className="w-5 h-5 animate-spin text-point" />
                                        ) : (
                                            <SendHorizontal
                                                className="w-[18px] h-[18px]"
                                                fill={
                                                    testInput.trim() && !isLoading
                                                        ? 'hsl(var(--point))'
                                                        : 'hsl(var(--text-500))'
                                                }
                                                stroke={
                                                    testInput.trim() && !isLoading
                                                        ? 'hsl(var(--point))'
                                                        : 'hsl(var(--text-500))'
                                                }
                                            />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
